'use client';

import { useMemo } from 'react';
import type { PortableTextBlock } from 'sanity';
import { stegaClean } from '@sanity/client/stega';

import type { NewPricingCopy } from '@/components/pageComponents/newPricing/copy';
import type { PageRizAffiliateSchema } from '@/sanity/pages/PageRizAffiliateTemplate';
import { measuredRichText, V3_HIGHLIGHT } from './richtext';
import { RevealEyebrow, RevealRichText, RevealStyles, RevealWords } from './about/reveal';

const HEADING_CLASS = `${V3_HIGHLIGHT} richtext richtext-center prose-headings:text-black prose-headings:font-geist prose-headings:font-[500] prose-headings:text-[4.5rem] prose-headings:leading-[1.15] prose-headings:tracking-[-0.225rem] lg:prose-headings:text-[6rem] lg:prose-headings:tracking-[-0.3rem] [--hl-tracking:-0.03em] [--hl-ml:0.33em] [--hl-ctr:0.1834rem] [--hl-pl:0rem] [--hl-ext:0rem] [--hl-wash:5rem] [--hl-dy:-0.75rem] lg:[--hl-ctr:0.3178rem] lg:[--hl-pl:0.8rem] lg:[--hl-pr:0.975rem] lg:[--hl-wash:6.75rem] lg:[--hl-dy:0.25rem]`;

function fallbackHeading(copy: NewPricingCopy): PortableTextBlock[] {
  const [lead, ...rest] = stegaClean(copy.heroTitleAccent).trim().split(' ');
  const accent = rest.join(' ');
  const span = (text: string, marks: string[] = []) => ({ _type: 'span', _key: text, text, marks });
  const block = (key: string, children: ReturnType<typeof span>[]) => ({
    _type: 'block',
    _key: key,
    style: 'h2',
    markDefs: [],
    children,
  });
  const highlight = ['darkGreenSerif', 'lightGreenHighlight'];
  return [
    block('l1', [span(copy.heroTitleLine1)]),
    block('l2', accent ? [span(`${lead} `), span(accent, highlight)] : [span(lead, highlight)]),
  ] as unknown as PortableTextBlock[];
}

export default function PricingHeader({ data, copy }: { data: PageRizAffiliateSchema; copy: NewPricingCopy }) {
  const chip = data?.pricingChip || copy.heroEyebrow;
  const subtitle = data?.pricingSubtitle || stegaClean(copy.heroSub).replace(/\.\s+/, '.\n');
  const heading = useMemo(
    () => (data?.pricingHeading?.length ? data.pricingHeading : fallbackHeading(copy)),
    [data?.pricingHeading, copy],
  );

  return (
    <div className="flex flex-col items-center w-full text-center gap-[2.5rem]">
      <RevealStyles />
      <div className="flex flex-col items-center gap-[2rem] lg:gap-[0.625rem]">
        <RevealEyebrow text={chip} />
        <RevealRichText
          value={heading}
          components={measuredRichText}
          className={HEADING_CLASS}
        />
      </div>
      <RevealWords
        text={subtitle}
        className="m-0 whitespace-pre-line font-geist font-[400] text-black/70 text-[2rem] leading-[1.33] w-[min(46rem,100%)] lg:text-[2.25rem] lg:w-[min(51.875rem,100%)]"
      />
    </div>
  );
}
